import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useEffect, useReducer, useState } from "react";
import { useAnalyzerSerialAutocomplete } from "../hooks/useAnalyzerSerialAutocomplete";
import { Form } from "../utils/sharedTypes";

const AddItemDialogWithSecondaryDialog = ({
  addModalOpen,
  setAddModalOpen,
  secondaryAddModalOpen,
  setSecondaryAddModalOpen,
  secondaryItemName,
  addFormInitialState,
  itemCreator,
  itemValidator,
  formInstructions,
  handlerMiddleware,
  children,
  itemName,
}: {
  addModalOpen: boolean;
  setAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  secondaryAddModalOpen: boolean;
  setSecondaryAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  secondaryItemName: string;
  addFormInitialState: object;
  itemCreator: Function;
  itemValidator: (item: any) => boolean;
  formInstructions: string;
  handlerMiddleware: (
    name: string,
    value: string,
    state: Form,
    autoboxValue?: object,
    autoboxLabel?: string
  ) => object;
  children: Function;
  itemName?: string;
}) => {
  const analyzerAutoComplete = useAnalyzerSerialAutocomplete();
  const [submitted, setSubmitted] = useState(false);
  const [addResults, setAddResults] = useState<string>("");
  const [addFormState, setAddFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    addFormInitialState
  );
  // If initial state passed to dialog changes, reset the form state
  useEffect(() => {
    setAddFormState(addFormInitialState);
  }, [addFormInitialState]);
  const handleFormChange = (
    event: any,
    autoboxValue?: object,
    autoboxLabel?: string
  ) => {
    const { name, value } = event.target;
    const middlewareResult = handlerMiddleware(
      name,
      value,
      addFormState,
      autoboxValue,
      autoboxLabel
    );
    const autoCompleteResults = analyzerAutoComplete(name, value);
    setAddFormState({
      [name]: value,
      ...autoCompleteResults,
      ...middlewareResult,
    });
  };
  // If user closes the secondary dialog, reset form state
  useEffect(() => {
    if (!secondaryAddModalOpen) setAddFormState(addFormInitialState);
  }, [secondaryAddModalOpen, addFormInitialState]);

  const handleEnter = (event: any) => {};

  const handleAddModalAdd = async (event: any) => {
    const result = await itemCreator(addFormState);
    if ("error" in result) {
      setAddResults(
        // @ts-ignore
        `Failed to create item. ${result.error.data.message}`
      );
    } else {
      setAddResults("Item created.");
    }
    setAddFormState(addFormInitialState);
    setSubmitted(true);
  };
  const handleAddModalAddWithSecondary = async (event: any) => {
    const result = await itemCreator(addFormState);
    if ("error" in result) {
      setAddResults(
        // @ts-ignore
        `Failed to create item. ${result.error.data.message}`
      );
    } else {
      setAddResults("Item created.");
    }
    setSecondaryAddModalOpen(true);
    setSubmitted(true);
  };
  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setAddResults("");
    setSubmitted(false);
    setAddFormState(addFormInitialState);
  };
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={addModalOpen}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        Add {itemName ? itemName : "Item"}
      </DialogTitle>
      <DialogContent>
        {(!submitted || secondaryAddModalOpen) &&
          children(addFormState, handleFormChange, handleEnter)}
        {!submitted && !itemValidator(addFormState) && (
          <Typography color="error">{formInstructions}</Typography>
        )}
        {submitted && <Typography>{addResults}</Typography>}
      </DialogContent>
      {submitted ? (
        <DialogActions>
          <Button onClick={handleAddModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={handleAddModalClose} color="primary">
            Cancel
          </Button>
          {itemValidator(addFormState) && (
            <>
              <Button onClick={handleAddModalAddWithSecondary} color="primary">
                Save With {secondaryItemName}
              </Button>
              <Button onClick={handleAddModalAdd} color="primary">
                Save Without {secondaryItemName}
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddItemDialogWithSecondaryDialog;
